/**
 * Supplier-decline setting (CR-5892).
 *
 * Relocates "Allow Supplier Decline" out of Platform Admin (Helix-only) into
 * client-facing Admin Setup → Purchasing. Reads/writes the per-tenant flag via
 * the client-editable company-feature-flags endpoint, so a customer admin can
 * manage it for their own tenant without a Valuechain operator.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, ShieldCheck } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

export function SupplierDeclineSetting() {
  const [loading, setLoading] = useState(true);
  const [allowDecline, setAllowDecline] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getClientFeatureFlags();
        setAllowDecline(!!data.allow_supplier_decline);
      } catch (err: unknown) {
        console.error('Error loading supplier decline setting:', err);
        toast.error('Failed to load the supplier decline setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = allowDecline;
    setAllowDecline(checked);
    setSaving(true);
    try {
      const { data } = await platformSettingsAPI.patchClientFeatureFlags({
        allow_supplier_decline: checked,
      });
      setAllowDecline(!!data.allow_supplier_decline);
      toast.success(
        checked ? 'Suppliers can now decline new PO lines' : 'Supplier decline disabled',
      );
    } catch (err: unknown) {
      console.error('Error saving supplier decline setting:', err);
      toast.error('Failed to save the supplier decline setting');
      setAllowDecline(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Supplier Decline
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Control whether suppliers can decline new purchase order lines.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="allow-supplier-decline" className="text-sm font-medium">
              Allow Supplier Decline
            </Label>
            <p className="text-xs text-muted-foreground">
              Allow suppliers to decline (reject) new PO lines. When disabled, suppliers can only
              accept or propose changes.
            </p>
          </div>
          <Switch
            id="allow-supplier-decline"
            checked={allowDecline}
            disabled={saving}
            onCheckedChange={handleToggle}
          />
        </div>
      </CardContent>
    </Card>
  );
}
